/**
 * ============================================================================
 * Bulk Order Grid Guide  (ISOLATED, ADDITIVE-ONLY — no existing logic touched)
 * ============================================================================
 * Purpose (visual aid only): when a shopper focuses or hovers a quantity cell
 * in the bulk warehouse grid, show:
 *   1) A floating tooltip with WAREHOUSE + SIZE + PRICE for that exact cell.
 *   2) A row + column "cross" highlight so they can see where they are.
 *
 * This file NEVER:
 *   - reads or writes input values
 *   - calls imposeMinMax / add-to-cart / the Back-In-Stock (BIS) flow
 *   - adds/removes anything the cart form submits
 * It only toggles CSS classes and moves a body-level tooltip element.
 *
 * How it survives the grid's innerHTML re-render (color switch, API load):
 *   All handlers are DELEGATED on `document`, and the tooltip lives on <body>,
 *   so nothing breaks when product-bulk-order-warehouse.js rebuilds the grid.
 *
 * DOM contract it relies on (from product-bulk-order-warehouse.js):
 *   .Color_warehouse_master_block          -> one per color
 *     .each_warehouse_block  (FIRST = Size/Price header row)
 *       .warehouse_name                    -> "Warehouse name" (first text node)
 *       ul.warehouse_ul > li.ai_store      -> one cell per size, fixed order
 *         input.quantity-input             -> present in every warehouse-row cell
 *   Header cells contain .size_block (size) and .price_small (price).
 *   Column index = index of the li.ai_store within its ul; the header cell at
 *   the same index gives the size + price.
 * ============================================================================
 */
(function () {
  "use strict";

  if (window.__bulkGridGuideLoaded) return;
  window.__bulkGridGuideLoaded = true;

  // ---------------------------------------------------------------------------
  // FEATURE FLAGS — toggle each part independently.
  //
  // ENABLE_CROSS_HIGHLIGHT: row + column + active-cell "cross" shading.
  //   FALSE = no hover/focus shading (grid looks like the old way).
  //
  // ENABLE_TOOLTIP: the floating Warehouse + Size + Price tooltip.
  //   FALSE = no tooltip.
  //
  // Set BOTH to FALSE to fully disable this helper (pure old behavior).
  // All code below is kept intact; the flags simply short-circuit it.
  // ---------------------------------------------------------------------------
  var ENABLE_CROSS_HIGHLIGHT = false;
  var ENABLE_TOOLTIP = false;

  var CLASS_ROW = "cbg-row";
  var CLASS_COL = "cbg-col";
  var CLASS_ACTIVE = "cbg-active";

  var tooltip = null;
  var focusedCell = null; // the cell currently being typed in (persistent)
  var repositionScheduled = false;

  /* ---------- one-time styles (self-contained, no external CSS) ---------- */
  function injectStyles() {
    if (document.getElementById("cbg-styles")) return;
    var css = [
      ".cbg-row{background-color:rgba(37,99,235,0.07)!important;}",
      "li.ai_store.cbg-col{background-color:rgba(37,99,235,0.07)!important;border-radius:4px;}",
      "li.ai_store.cbg-active{background-color:rgba(37,99,235,0.12)!important;box-shadow:inset 0 0 0 2px #93c5fd;border-radius:4px;}",
      ".cbg-tooltip{position:absolute;z-index:99999;top:0;left:0;max-width:240px;",
      "background:#1f2937;color:#fff;padding:7px 10px;border-radius:8px;",
      "font-size:12px;line-height:1.35;font-weight:600;pointer-events:none;",
      "box-shadow:0 6px 20px rgba(0,0,0,0.25);opacity:0;visibility:hidden;",
      "transition:opacity .12s ease;transform:translate(-50%,0);white-space:nowrap;}",
      ".cbg-tooltip.cbg-show{opacity:1;visibility:visible;}",
      ".cbg-tooltip .cbg-tt-wh{display:block;font-weight:700;}",
      ".cbg-tooltip .cbg-tt-meta{display:block;font-weight:500;opacity:.9;margin-top:2px;}",
      ".cbg-tooltip .cbg-tt-price{color:#fff;font-weight:800;opacity:1;font-size:13px;}",
      ".cbg-tooltip:after{content:'';position:absolute;left:50%;transform:translateX(-50%);",
      "border:6px solid transparent;}",
      ".cbg-tooltip.cbg-pos-top:after{top:100%;border-top-color:#1f2937;}",
      ".cbg-tooltip.cbg-pos-bottom:after{bottom:100%;border-bottom-color:#1f2937;}",
    ].join("");
    var style = document.createElement("style");
    style.id = "cbg-styles";
    style.textContent = css;
    document.head.appendChild(style);
  }

  function ensureTooltip() {
    if (tooltip) return tooltip;
    tooltip = document.createElement("div");
    tooltip.className = "cbg-tooltip";
    tooltip.setAttribute("role", "status");
    tooltip.setAttribute("aria-live", "polite");
    tooltip.innerHTML =
      '<span class="cbg-tt-wh"></span>' +
      '<span class="cbg-tt-meta"><span class="cbg-tt-size"></span> &middot; ' +
      '<span class="cbg-tt-price"></span></span>';
    document.body.appendChild(tooltip);
    return tooltip;
  }

  /* ---------- read the first text node (warehouse/size name) ---------- */
  function firstText(el) {
    if (!el) return "";
    for (var i = 0; i < el.childNodes.length; i++) {
      var n = el.childNodes[i];
      if (n.nodeType === 3 && n.textContent.trim()) return n.textContent.trim();
    }
    return (el.textContent || "").trim();
  }

  /* ---------- derive warehouse/size/price for a given cell ---------- */
  function getCellInfo(cell) {
    var row = cell.closest(".each_warehouse_block");
    var ul = cell.closest("ul.warehouse_ul");
    var colorBlock = cell.closest(".Color_warehouse_master_block");
    if (!row || !ul || !colorBlock) return null;

    // Only warehouse rows have inputs; header row cells do not.
    if (!cell.querySelector("input.quantity-input")) return null;

    var cells = [];
    for (var i = 0; i < ul.children.length; i++) {
      if (ul.children[i].classList && ul.children[i].classList.contains("ai_store")) {
        cells.push(ul.children[i]);
      }
    }
    var colIndex = cells.indexOf(cell);
    if (colIndex < 0) return null;

    // First .each_warehouse_block in this color block is the Size/Price header.
    var headerRow = colorBlock.querySelector(".each_warehouse_block");
    var sizeText = "";
    var priceText = "";
    if (headerRow) {
      var headerCells = headerRow.querySelectorAll("ul.warehouse_ul > li.ai_store");
      var hc = headerCells[colIndex];
      if (hc) {
        var sizeEl = hc.querySelector(".size_block");
        var priceEl = hc.querySelector(".price_small");
        sizeText = sizeEl ? sizeEl.textContent.trim() : "";
        priceText = priceEl ? priceEl.textContent.trim() : "";
      }
    }

    return {
      row: row,
      colorBlock: colorBlock,
      colIndex: colIndex,
      warehouse: firstText(row.querySelector(".warehouse_name")),
      size: sizeText,
      price: priceText,
    };
  }

  /* ---------- clear all guide highlighting ---------- */
  function clearHighlight() {
    if (!ENABLE_CROSS_HIGHLIGHT) return; // tooltip-only mode: nothing to clear
    var rows = document.querySelectorAll("." + CLASS_ROW);
    for (var i = 0; i < rows.length; i++) rows[i].classList.remove(CLASS_ROW);
    var cols = document.querySelectorAll("." + CLASS_COL);
    for (var j = 0; j < cols.length; j++) cols[j].classList.remove(CLASS_COL);
    var act = document.querySelectorAll("." + CLASS_ACTIVE);
    for (var k = 0; k < act.length; k++) act[k].classList.remove(CLASS_ACTIVE);
  }

  /* ---------- paint row + column cross for a cell ---------- */
  function applyHighlight(cell, info) {
    if (!ENABLE_CROSS_HIGHLIGHT) return; // tooltip-only mode: skip all shading
    info.row.classList.add(CLASS_ROW);
    var blocks = info.colorBlock.querySelectorAll(".each_warehouse_block");
    for (var i = 0; i < blocks.length; i++) {
      var lis = blocks[i].querySelectorAll("ul.warehouse_ul > li.ai_store");
      var target = lis[info.colIndex];
      if (target) target.classList.add(CLASS_COL);
    }
    cell.classList.add(CLASS_ACTIVE);
  }

  /* ---------- position + fill the tooltip above (or below) the cell ---------- */
  function showTooltip(cell, info) {
    if (!ENABLE_TOOLTIP) return; // tooltip disabled via flag
    var tt = ensureTooltip();
    var anchor = cell.querySelector("input.quantity-input") || cell;
    tt.querySelector(".cbg-tt-wh").textContent = info.warehouse || "";
    tt.querySelector(".cbg-tt-size").textContent = info.size ? "Size " + info.size : "";
    tt.querySelector(".cbg-tt-price").textContent = info.price || "";

    // Make it measurable, then position.
    tt.classList.add("cbg-show");
    var rect = anchor.getBoundingClientRect();
    var ttRect = tt.getBoundingClientRect();
    var gap = 10;

    // Scroll offsets: we position in DOCUMENT coordinates (position:absolute) so
    // the tooltip stays glued to the field regardless of when the page/keyboard
    // scrolls. A viewport-relative (position:fixed) value goes stale on mobile
    // because the browser auto-scrolls the focused field above the keyboard
    // AFTER focusin fires. rect.top + scrollY is scroll-invariant, so it doesn't.
    var scrollX = window.pageXOffset || document.documentElement.scrollLeft || 0;
    var scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;

    // Decide above/below using the VISIBLE viewport (what the user sees), but
    // emit the resulting position in document coordinates.
    var placeTop = rect.top - ttRect.height - gap >= 8;
    var top = placeTop
      ? rect.top + scrollY - ttRect.height - gap
      : rect.bottom + scrollY + gap;

    var left = rect.left + scrollX + rect.width / 2;
    // Keep within the visible viewport horizontally (in document coords).
    var half = ttRect.width / 2;
    var minLeft = scrollX + half + 8;
    var maxLeft = scrollX + window.innerWidth - half - 8;
    if (left < minLeft) left = minLeft;
    if (left > maxLeft) left = maxLeft;

    tt.style.top = Math.round(top) + "px";
    tt.style.left = Math.round(left) + "px";
    tt.classList.toggle("cbg-pos-top", placeTop);
    tt.classList.toggle("cbg-pos-bottom", !placeTop);
  }

  function hideTooltip() {
    if (tooltip) tooltip.classList.remove("cbg-show");
  }

  /* ---------- render guide for a cell (persistent = focused) ---------- */
  function renderFor(cell, persistent) {
    var info = getCellInfo(cell);
    if (!info) return false;
    clearHighlight();
    applyHighlight(cell, info);
    showTooltip(cell, info);
    if (persistent) focusedCell = cell;
    return true;
  }

  function clearAll() {
    clearHighlight();
    hideTooltip();
  }

  /* ---------- reposition tooltip on scroll/resize while focused ---------- */
  function scheduleReposition() {
    if (repositionScheduled) return;
    repositionScheduled = true;
    window.requestAnimationFrame(function () {
      repositionScheduled = false;
      if (focusedCell && document.contains(focusedCell)) {
        var info = getCellInfo(focusedCell);
        if (info) showTooltip(focusedCell, info);
      }
    });
  }

  /* ---------- delegated events ---------- */
  // FOCUS (keyboard, tap, click into field) -> persistent guide.
  document.addEventListener("focusin", function (e) {
    var input = e.target.closest ? e.target.closest("input.quantity-input") : null;
    if (!input) return;
    var cell = input.closest("li.ai_store");
    if (cell) renderFor(cell, true);
  });

  document.addEventListener("focusout", function (e) {
    var input = e.target.closest ? e.target.closest("input.quantity-input") : null;
    if (!input) return;
    focusedCell = null;
    clearAll();
  });

  // HOVER (mouse only) -> transient guide; restores focused cell on leave.
  document.addEventListener("pointerover", function (e) {
    if (e.pointerType && e.pointerType !== "mouse") return; // touch is handled by focus
    var cell = e.target.closest ? e.target.closest("li.ai_store") : null;
    if (cell && cell.querySelector("input.quantity-input")) {
      renderFor(cell, false);
    } else if (!focusedCell) {
      clearAll();
    } else {
      // moved off a cell but a field is focused -> restore its highlight
      var info = getCellInfo(focusedCell);
      if (info) {
        clearHighlight();
        applyHighlight(focusedCell, info);
        showTooltip(focusedCell, info);
      }
    }
  });

  document.addEventListener("pointerout", function (e) {
    if (e.pointerType && e.pointerType !== "mouse") return;
    var toEl = e.relatedTarget;
    var stillInCell = toEl && toEl.closest && toEl.closest("li.ai_store");
    if (stillInCell) return; // moving between cells; next pointerover handles it
    if (focusedCell) {
      var info = getCellInfo(focusedCell);
      if (info) {
        clearHighlight();
        applyHighlight(focusedCell, info);
        showTooltip(focusedCell, info);
      }
    } else {
      clearAll();
    }
  });

  window.addEventListener("scroll", scheduleReposition, true);
  window.addEventListener("resize", scheduleReposition);

  // Mobile keyboard show/hide changes the visible viewport WITHOUT firing a
  // normal window scroll — listen to visualViewport so the tooltip re-snaps.
  if (window.visualViewport) {
    window.visualViewport.addEventListener("scroll", scheduleReposition);
    window.visualViewport.addEventListener("resize", scheduleReposition);
  }

  /* ---------- init ---------- */
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", injectStyles);
  } else {
    injectStyles();
  }
})();
